import { useEffect, useRef } from "react";
import { useSim } from "../lib/store";

// Web Audio synthesized low-frequency seismic rumble + swelling "Volcanic Thunder".
export function AudioEngine() {
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);
  const noiseBufRef = useRef(null);
  const lastThunderRef = useRef(0);
  const lastSirenRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const ensureNoise = (ctx) => {
      if (noiseBufRef.current) return noiseBufRef.current;
      const len = Math.floor(ctx.sampleRate * 2);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      noiseBufRef.current = buf;
      return buf;
    };

    // A rolling thunder crack: filtered noise sweep + deep sub boom.
    const playThunder = (ctx, intensity) => {
      const t = ctx.currentTime;
      // noise body
      const src = ctx.createBufferSource();
      src.buffer = ensureNoise(ctx);
      src.playbackRate.value = 0.7 + Math.random() * 0.3;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.setValueAtTime(900, t);
      lp.frequency.exponentialRampToValueAtTime(140, t + 1.6);
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.0001, t);
      ng.gain.exponentialRampToValueAtTime(0.32 * intensity, t + 0.08);
      ng.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
      src.connect(lp).connect(ng).connect(ctx.destination);
      src.start(t);
      src.stop(t + 2);
      // sub boom
      const boom = ctx.createOscillator();
      boom.type = "sine";
      boom.frequency.setValueAtTime(70, t);
      boom.frequency.exponentialRampToValueAtTime(28, t + 1.3);
      const bg = ctx.createGain();
      bg.gain.setValueAtTime(0.0001, t);
      bg.gain.exponentialRampToValueAtTime(0.28 * intensity, t + 0.05);
      bg.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
      boom.connect(bg).connect(ctx.destination);
      boom.start(t);
      boom.stop(t + 1.7);
    };

    // Distant emergency siren wail (two-tone rise/fall) — survivor response after a collapse.
    const playSiren = (ctx, intensity) => {
      const t = ctx.currentTime;
      const dur = 2.6;
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(680, t);
      osc.frequency.linearRampToValueAtTime(1120, t + dur * 0.5);
      osc.frequency.linearRampToValueAtTime(680, t + dur);
      const det = ctx.createOscillator(); // slight detune body for realism
      det.type = "sawtooth";
      det.frequency.setValueAtTime(676, t);
      det.frequency.linearRampToValueAtTime(1112, t + dur * 0.5);
      det.frequency.linearRampToValueAtTime(676, t + dur);
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 950;
      bp.Q.value = 3.5;
      const lp = ctx.createBiquadFilter(); // muffle so it reads "distant"
      lp.type = "lowpass";
      lp.frequency.value = 1400;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.07 * intensity, t + 0.25);
      g.gain.setValueAtTime(0.07 * intensity, t + dur - 0.35);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(bp);
      det.connect(bp);
      bp.connect(lp).connect(g).connect(ctx.destination);
      osc.start(t); osc.stop(t + dur + 0.05);
      det.start(t); det.stop(t + dur + 0.05);
    };

    const loop = () => {
      const st = useSim.getState();
      const ctx = ctxRef.current;

      // aggregate running magnitude across simultaneous events
      let mag = st.magnitude;
      let erupting = false;
      if (st.simulating) {
        const elapsed = (performance.now() - st.simStart) / 1000;
        let m = 0;
        for (const e of st.events) {
          if (elapsed <= e.duration + 0.5) m = Math.max(m, e.magnitude);
          if (e.activeEruption) erupting = true;
        }
        if (m > 0) mag = m;
      }

      // Shared disaster window (survivor sirens + sweeping searchlights): a Mw>=7.5
      // rupture past mid-shake arms a ~10s aftermath response. rebuildCity() zeroes it.
      if (st.simulating && mag >= 7.5 && st.simProgress > 0.35) {
        window.__disasterUntil = performance.now() + 10000;
      }

      if (st.audioOn) {
        if (!ctx) {
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            const c = new AC();
            const osc = c.createOscillator();
            const gain = c.createGain();
            osc.type = "sine";
            osc.frequency.value = 48;
            gain.gain.value = 0;
            osc.connect(gain).connect(c.destination);
            osc.start();
            ctxRef.current = c;
            oscRef.current = osc;
            gainRef.current = gain;
          } catch (e) {
            /* ignore */
          }
        } else {
          ctx.resume?.();
          const eventBoost = 1 + Math.min(2, Math.max(0, st.events.length - 1)) * 0.28;
          const targetFreq = 40 + (mag - 4) * 7;
          const env = st.simulating ? Math.sin(Math.min(st.simProgress, 1) * Math.PI) * 0.7 + 0.35 : 0.12;
          const targetGain = ((mag - 4) / 5.5) * 0.22 * env * eventBoost;
          if (oscRef.current) oscRef.current.frequency.setTargetAtTime(targetFreq, ctx.currentTime, 0.2);
          if (gainRef.current) gainRef.current.gain.setTargetAtTime(targetGain, ctx.currentTime, 0.25);

          // Volcanic Thunder — periodic swelling cracks while any vent is erupting.
          if (st.simulating && erupting) {
            const now = performance.now();
            if (now - lastThunderRef.current > 3200 + Math.random() * 2400) {
              lastThunderRef.current = now;
              const intensity = Math.min(1.2, 0.6 + (mag - 6) * 0.14);
              playThunder(ctx, Math.max(0.4, intensity));
            }
          }

          // Survivor sirens — distant emergency wail during the collapse aftermath window.
          if (performance.now() < (window.__disasterUntil || 0)) {
            const now = performance.now();
            if (now - lastSirenRef.current > 2900) {
              lastSirenRef.current = now;
              playSiren(ctx, Math.min(1, 0.75 + (mag - 7.5) * 0.15));
            }
          }
        }
      } else if (ctxRef.current) {
        if (gainRef.current) gainRef.current.gain.setTargetAtTime(0, ctxRef.current.currentTime, 0.15);
        ctxRef.current.suspend?.(); // absolute mute — halts the audio graph entirely
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafRef.current);
      try {
        oscRef.current?.stop();
        ctxRef.current?.close();
      } catch (e) {
        /* ignore */
      }
    };
  }, []);

  return null;
}
